import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { listWebhookLogs } from "@/api/ezyvet";
import { EzyVetSettingsTabs } from "@/components/ezyvet/EzyVetSettingsTabs";
import { PayloadInspector } from "@/components/ezyvet/PayloadInspector";
import { WebhookStatusBadge } from "@/components/ezyvet/WebhookStatusBadge";
import { QueryError } from "@/components/ui/QueryError";
import type { ApiError } from "@/types";
import type { Page, WebhookEventStatus, WebhookLogEntry } from "@/types/ezyvet";

const PAGE_SIZE = 25;
const STATUSES: WebhookEventStatus[] = ["queued", "processed", "ignored", "failed"];

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function processingTime(e: WebhookLogEntry): string {
  if (!e.processed_at) return "—";
  const ms = Date.parse(e.processed_at) - Date.parse(e.received_at);
  return Number.isFinite(ms) && ms >= 0 ? (ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`) : "—";
}

/** /settings/integrations/ezyvet/logs: inbound webhook deliveries, newest first. */
export function EzyVetWebhookLogsPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<WebhookEventStatus | "">("");
  const [page, setPage] = useState(0);
  const [live, setLive] = useState(true);
  const [inspecting, setInspecting] = useState<WebhookLogEntry | null>(null);
  const query = useDebounced(search.trim(), 300);

  // A new filter starts from the first page
  useEffect(() => setPage(0), [query, status]);

  const logs = useQuery<Page<WebhookLogEntry>, ApiError>({
    queryKey: ["ezyvet", "webhook-logs", { query, status, page }],
    queryFn: () => listWebhookLogs({ limit: PAGE_SIZE, offset: page * PAGE_SIZE, search: query || undefined, status: status || undefined }),
    placeholderData: keepPreviousData,
    // Live stream: poll the first page only, so paging back through history stays put
    refetchInterval: (q) => (live && page === 0 && q.state.error?.status !== 404 ? 10_000 : false),
  });

  const data = logs.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div>
      <div className="page-header">
        <h1>ezyVet webhook log</h1>
        <p className="page-sub">Every webhook ezyVet delivered, and what VetGuard did with it. Select a row to inspect its payload.</p>
        <EzyVetSettingsTabs />
      </div>

      <div className="toolbar">
        <input
          type="search"
          className="grow"
          placeholder="Search event id or type, e.g. consult.created"
          aria-label="Search webhooks"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value as WebhookEventStatus | "")}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <label className="toggle-row toggle-row--inline">
          <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
          <span className={`live-status ${live && page === 0 ? "live" : ""}`}>
            <span className="live-dot" aria-hidden="true" />
            Live
          </span>
        </label>
      </div>

      {logs.isLoading && (
        <div className="card loading">
          <div className="spinner" />
          <div>Loading webhook log…</div>
        </div>
      )}
      {logs.error && !data && (
        <QueryError
          error={logs.error}
          what="the webhook log"
          onRetry={() => logs.refetch()}
          notFound="The webhook log isn't available on this server yet."
        />
      )}

      {data && data.items.length === 0 && (
        <div className="card muted">
          {query || status ? "No webhooks match these filters." : "No webhooks received yet. Set up the endpoint on the Settings tab."}
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className={`card table-card ${logs.isFetching && logs.isPlaceholderData ? "is-stale" : ""}`}>
          <table className="table table--dense">
            <caption className="sr-only">Inbound ezyVet webhooks</caption>
            <thead>
              <tr>
                <th scope="col">Received</th>
                <th scope="col">Event</th>
                <th scope="col">Event id</th>
                <th scope="col">Status</th>
                <th scope="col" className="num">
                  Processing
                </th>
                <th scope="col">Error</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <tr
                  key={e.id}
                  className="clickable"
                  tabIndex={0}
                  onClick={() => setInspecting(e)}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" || ev.key === " ") {
                      ev.preventDefault();
                      setInspecting(e);
                    }
                  }}
                  aria-label={`Inspect ${e.event_type ?? "event"} ${e.event_id}`}
                >
                  <td className="nowrap">{new Date(e.received_at).toLocaleString()}</td>
                  <td className="mono">{e.event_type ?? "unknown"}</td>
                  <td className="mono truncate" title={e.event_id}>
                    {e.event_id}
                  </td>
                  <td>
                    <WebhookStatusBadge status={e.status} />
                  </td>
                  <td className="num mono">{processingTime(e)}</td>
                  <td className="truncate error-text" title={e.error ?? undefined}>
                    {e.error ?? ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > PAGE_SIZE && (
        <div className="pager" role="navigation" aria-label="Webhook log pages">
          <button className="btn btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            ← Newer
          </button>
          <span className="muted">
            Page {page + 1} of {pages} · {data.total} events
          </span>
          <button className="btn btn-sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
            Older →
          </button>
        </div>
      )}
      {logs.error && data && (
        <p className="field-error" role="status">
          Showing the last loaded results; refreshing failed: {logs.error.message}
        </p>
      )}

      <PayloadInspector entry={inspecting} onClose={() => setInspecting(null)} />
    </div>
  );
}
